import { useTranslation } from "react-i18next";
import { Typography } from "@material-ui/core";
import { EnableButton, DisabledLabel, StorageAccountsLink } from "./styled";
import { isIndividual, isOrganization } from "./utils";

import { StorageAccountItem } from "../types";
import StorageProviderLabel from "components/StorageProviderLabel";

interface Props {
  item: StorageAccountItem;
  onLabelClick: (item: StorageAccountItem) => void;
}

export default function AccountOptionLabel({ item, onLabelClick }: Props) {
  const { t } = useTranslation("components");
  const { option, storageAccount } = item;
  const { authentication, provider } = storageAccount;
  const { needsReAuth, subTitle, missingRefreshToken } = option;
  const actionLabel = t("storageAccounts.option.authorize");
  const actionTestId = "storage__authorize-account-btn";

  function IndividualLabel() {
    return (
      <Typography variant="caption">
        {t(subTitle)}
        {needsReAuth &&
          t("storageAccounts.option.notSynced", {
            pre: " - ",
          })}
      </Typography>
    );
  }

  if (provider === "DROPBOX") {
    return (
      <Typography data-testid="rules__synced-label" variant="caption">
        <StorageProviderLabel value="DROPBOX" />
      </Typography>
    );
  }

  if (provider === "MXHERO_STORAGE") {
    return (
      <Typography data-testid="rules__synced-label" variant="caption">
        {needsReAuth ? t("common:disabled") : t("common:enabled")}
      </Typography>
    );
  }

  // Accounts that do not need re-auth show their plain label, even when the
  // consumer vetoed them: re-authorizing would not fix anything there.
  if (!needsReAuth) {
    if (isIndividual(authentication)) return <IndividualLabel />;

    return (
      <Typography data-testid="rules__synced-label" variant="caption">
        {t("storageAccounts.option.synced")}
      </Typography>
    );
  }

  return (
    <>
      {missingRefreshToken ? (
        <DisabledLabel
          data-testid="storage__revalidation-required-label"
          variant="caption"
        >
          {t("storageAccounts.option.missingRefreshToken")}
        </DisabledLabel>
      ) : (
        <>
          {isOrganization(authentication) && (
            <DisabledLabel
              data-testid="storage__not-synced-label"
              variant="caption"
            >
              {t("storageAccounts.option.notSynced")}
            </DisabledLabel>
          )}
          {isIndividual(authentication) && <IndividualLabel />}
        </>
      )}
      <EnableButton
        size="small"
        color="primary"
        disableElevation
        data-testid={actionTestId}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onLabelClick(item);
        }}
        variant="contained"
      >
        {actionLabel}
      </EnableButton>
      {isIndividual(authentication) && (
        <StorageAccountsLink
          onClick={e => {
            e.stopPropagation();
            window.open("/integrations?tab=ACCOUNTS", "_blank", "noopener");
          }}
        >
          {t("storageAccounts.option.goToStorage")}
        </StorageAccountsLink>
      )}
    </>
  );
}
